import { Grid, TextField, Typography } from '@mui/material'
import { CompanyType } from '@planning/app/api'
import { CompanyAutocomplete, LocationCodeAutoComplete } from '@planning/components'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { OrderFormStore } from '../stores/OrderFormStore'

export const OrderClientDetailsSubForm = observer(
  ({ formStore }: { formStore: OrderFormStore }) => {
    const { t } = useTranslate()

    return (
      <Grid container spacing={2}>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <TextField
            {...formStore.referenceNumber.props}
            fullWidth
            label={`${t('bookingNo', 'Booking no.')}`}
            variant='outlined'
            type='text'
            data-cy={'order-reference-no'}
            onChange={e => {
              formStore.referenceNumber.setValue(e.target.value.toUpperCase())
            }}
            name='referenceNumber'
            sx={{ marginTop: 0 }}
          />
        </Grid>

        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <TextField
            {...formStore.billOfLadding.props}
            fullWidth
            label={`${t('blNumber', 'B/L no.')}`}
            variant='outlined'
            type='text'
            data-cy={'bill-of-ladding-no'}
            onChange={e => {
              formStore.billOfLadding.setValue(e.target.value.toUpperCase())
            }}
            name='billOfLadding'
            sx={{ marginTop: 0 }}
          />
        </Grid>

        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <TextField
            {...formStore.shippingOrder.props}
            fullWidth
            label={`${t('shippingOrderReference', 'Shipping order reference')}`}
            variant='outlined'
            type='text'
            data-cy={'shipping-order-reference'}
            onChange={e => {
              formStore.shippingOrder.setValue(e.target.value.toUpperCase())
            }}
            name='shippingOrder'
            sx={{ marginTop: 0 }}
          />
        </Grid>

        <Grid size={{ sm: 12 }}>
          <Typography variant='subtitle1'>{t('stakeholder', 'Stakeholder')}</Typography>
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }}>
          <CompanyAutocomplete
            label={t('forwarder', 'Forwarder')}
            dataCy='forwarder-autocomplete'
            selectedCompanyId={formStore.forwarderId.value ?? null}
            handleOnChange={company => formStore.forwarderId.setValue(company?.id ?? null)}
            filterByTypes={[CompanyType.Forwarder]}
          />
        </Grid>

        <Grid size={{ sm: 6, xs: 12 }}>
          <CompanyAutocomplete
            label={t('shipper', 'Shipper')}
            dataCy='shipper-autocomplete'
            selectedCompanyId={formStore.shipperId.value ?? null}
            handleOnChange={company => formStore.shipperId.setValue(company?.id ?? null)}
            filterByTypes={[CompanyType.Shipper]}
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <CompanyAutocomplete
            label={t('customerName', 'Customer name')}
            required={true}
            selectedCustomer={formStore.customer.value}
            handleOnChange={customer => {
              formStore.customer.setValue(customer)
            }}
            error={!!formStore.customer.error}
            helperText={formStore.customer.error ? t('fieldIsRequired', 'Field is required.') : ''}
            filterByTypes={[CompanyType.ShippingLine]}
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <CompanyAutocomplete
            label={t('consignee', 'Consignee')}
            dataCy='consignee-autocomplete'
            selectedCompanyShortName={formStore.consignee.value}
            handleOnChange={consignee => {
              formStore.consignee.setValue(consignee?.shortName ?? '')
            }}
            filterByTypes={[CompanyType.Consignee]}
          />
        </Grid>

        <Grid size={{ sm: 12 }}>
          <Typography variant='subtitle1'>{t('locations', 'Locations')}</Typography>
        </Grid>

        <Grid size={{ sm: 12 }}>
          <LocationCodeAutoComplete
            defaultValue={formStore.origin.value || undefined}
            onChange={value => formStore.origin.setValue(value)}
            label={t('origin', 'Origin')}
          />
        </Grid>

        <Grid size={{ sm: 6, xs: 12 }}>
          <LocationCodeAutoComplete
            defaultValue={formStore.inlandDestination.value || undefined}
            onChange={value => formStore.inlandDestination.setValue(value)}
            label={t('inlandDestination', 'Inland destination')}
          />
        </Grid>

        <Grid size={{ sm: 6, xs: 12 }}>
          <LocationCodeAutoComplete
            defaultValue={formStore.finalDestination.value || undefined}
            onChange={value => formStore.finalDestination.setValue(value)}
            label={t('finalDestination', 'Final destination')}
          />
        </Grid>

        <Grid size={{ sm: 6, xs: 12 }}>
          <LocationCodeAutoComplete
            defaultValue={formStore.portOfLoading.value || undefined}
            onChange={value => formStore.portOfLoading.setValue(value)}
            label={t('portOfLoading', 'Port of loading')}
          />
        </Grid>

        <Grid size={{ sm: 6, xs: 12 }}>
          <LocationCodeAutoComplete
            defaultValue={formStore.portOfDischarge.value || undefined}
            onChange={value => formStore.portOfDischarge.setValue(value)}
            label={t('portOfDischarge', 'Port of discharge')}
          />
        </Grid>
      </Grid>
    )
  },
)
